import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { ButtonType, DateUtils, DrawerComponent } from '@yupma/ui-kit';
import { ContactDocument, EntityAddFormField, EntityType, FieldDocument, FieldOption, FieldType, UserDocument } from 'server-model';
import { CRMContactDataService, CRMFieldDataService, IAMService, UIService } from 'src/app/services/services.module';

class UIEntityField {
  constructor(
    public name: string,
    public label: string,
    public description: string,
    public field_type: FieldType,
    public has_unique_value: boolean,
    public is_readonly_value: boolean,
    public required: boolean,
    public isInvalid: boolean = false, // Defaults to no error
    public errorText: string = "", // Defaults to no error message
    public option: FieldOption[] = []
  ) { }
}

@Component({
  selector: 'app-create-contact-drawer',
  templateUrl: './create-contact-drawer.component.html',
  styleUrl: './create-contact-drawer.component.scss'
})
export class CreateContactDrawerComponent implements OnChanges {
  @ViewChild(DrawerComponent) drawerComponent!: DrawerComponent;
  @Input() allFields: FieldDocument[] = [];
  @Input() entityAddFormFields: EntityAddFormField[] = [];
  @Input() allUsers: UserDocument[] = [];
  isLoading: boolean = false;
  buttonTypes = ButtonType;
  uiFields: UIEntityField[] = [];
  fieldTypes = FieldType;
  contactDocument: ContactDocument | null = null;

  constructor(
    private uiService: UIService,
    private iamService: IAMService,
    private crmContactDataService: CRMContactDataService,
  ) { }

  ngOnChanges() {
    this.initializeUIFields();
  }

  closeDrawer() {
    this.drawerComponent.closeDrawer();
    setTimeout(() => {
      this.uiService.closeAddContactDrawer();
    });
  }

  private initializeUIFields(): void {
    this.contactDocument = new ContactDocument({
      id: "",
      org_id: this.iamService.getOrganisationId(),
      is_archived: false,
      archived_on: 0,
      related_entities: {},
    });
    this.uiFields = this.entityAddFormFields.map(entityField => {
      const matchingField = this.allFields.find(field => field.name === entityField.field_name);
      if (!matchingField) {
        console.warn(`No matching field found for ${entityField.field_name}`);
        return null;
      }

      return {
        name: matchingField.name,
        label: matchingField.label,
        description: matchingField.description,
        field_type: matchingField.field_type,
        has_unique_value: matchingField.has_unique_value,
        is_readonly_value: matchingField.is_readonly_value,
        required: entityField.required,
        isInvalid: false, // Default value
        errorText: "", // Default value
        option: matchingField.option,
      };
    }).filter(field => field !== null) as UIEntityField[];
  }

  inputValueChange(value: any, field: UIEntityField) {
    field.isInvalid = false;
    field.errorText = "";

    // Update the contactDocument dynamically
    if (this.contactDocument) {
      this.contactDocument[field.name] = value;
    }

    // Perform custom validations (e.g., unique value validation)
    // if (field.has_unique_value) {
    //   this.checkUniqueValue(value, field);
    // }
  }

  // async checkUniqueValue(value: any, field: UIEntityField) {
  //   try {
  //     const organisationId = this.iamService.getOrganisationId();
  //     let isUnique = [];
  //     if (field.name === 'email' || field.name === 'phone') {
  //       if (value.length && value[0].value) {
  //         isUnique = await this.crmFieldDataService.validateField(organisationId, EntityType.CONTACT, [{ name: field.name, value: value[0].value }]);
  //       }
  //     } else {
  //       if (value) {
  //         isUnique = await this.crmFieldDataService.validateField(organisationId, EntityType.CONTACT, [{ name: field.name, value: value }]);
  //       }
  //     }
  //     if (isUnique.length) {
  //       field.isInvalid = true;
  //       field.errorText = `Value should be unique. Contact with same ${field.label} already exists`;
  //     }
  //   } catch (error) {
  //     console.log(error)
  //   }
  // }

  async saveContact() {
    try {
      if (this.contactDocument) {
        let hasErrors = false;
        this.uiFields.forEach(field => {
          // Check for required fields
          if (field.required && (!this.contactDocument![field.name] || this.contactDocument![field.name].length === 0) && !field.isInvalid) {
            field.isInvalid = true;
            field.errorText = "This field is required.";
            hasErrors = true;
          }

          // Check for invalid fields
          if (field.isInvalid) {
            hasErrors = true;
          }
        });

        if (!hasErrors) {
          this.isLoading = true;
          const uid = this.iamService.getUserId()
          if (!this.contactDocument['contact_owner']) {
            this.contactDocument['contact_owner'] = uid;
          }

          const currentDate = DateUtils.getCurrentTimeStamp();
          this.contactDocument['source'] = 'CRM ui';
          this.contactDocument['created_on'] = currentDate;
          this.contactDocument['created_by'] = uid;
          this.contactDocument['updated_on'] = currentDate;
          this.contactDocument['updated_by'] = uid;
          let savedContact = await this.crmContactDataService.createContact(this.contactDocument)
          this.uiService.showToast('Contact created');
          this.isLoading = false;
          this.uiService.contactAdded(savedContact.data);

          this.closeDrawer();
        }
      }
    } catch (error) {
      this.isLoading = false;
      console.log(error)
    }
  }

  invalidateField(isInvalid: boolean, field: UIEntityField) {
    if (isInvalid) {
      field.isInvalid = true;
      field.errorText = '';
    } else {
      field.isInvalid = false;
      field.errorText = '';
    }
  }
}
